/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
const crossedOut = {
    content: '""',
    display: 'block',
    position: 'absolute',
    height: '50px',
    width: '1px',
    transform: 'rotate(45deg)',
    backgroundColor: 'darkGray',
    borderLeft: '1px solid',
    borderColor: 'mediumGray',
    zIndex: 1
}

const blackFrame = {
    content: '""',
    display: 'block',
    position: 'absolute',
    height: '38px',
    width: '38px',
    border: '1px solid white',
    outline: '2px solid black',
    zIndex: 1
}

export default {
    baseStyle: () => ({
        swatchGroup: {
            flexDirection: 'column'
        },
        swatchLabel: {
            marginBottom: 3,
            fontSize: '11px'
        },
        swatch: {
            position: 'relative',
            backgroundColor: 'white',
            _focus: {
                outline: 'none',
                boxShadow: 'outline'
            },
            textDecoration: 'none'
        },
        swatchesWrapper: {
            flexWrap: 'wrap'
        }
    }),
    variants: {
        color: (props) => ({
            swatch: {
                height: '38px',
                width: '38px',
                borderRadius: 0,
                padding: 0,
                cursor: 'pointer',
                color: 'darkGray',
                borderWidth: ['white', '#FFF', '#FFFFFF', 'rgb(255, 255, 255)'].includes(
                    props.value
                )
                    ? '1px'
                    : 0,
                borderColor: 'borderGray',
                _active: {
                    background: 'transparent'
                },
                _before: props.disabled ? crossedOut : props.selected && blackFrame,
                _after: props.disabled && props.selected && blackFrame
            },
            swatchesWrapper: {
                gridGap: ['8px', null, '6.25px'],
                gridTemplateColumns: 'repeat(auto-fill, 38px)'
            }
        }),
        sizeRefinement: (props) => ({
            swatch: {
                height: '44px',
                px: '5px',
                width: 'calc(100% + 1px)',
                color: props.selected ? 'white' : 'darkGray',
                backgroundColor: props.selected ? 'fullBlack' : 'transparent',
                borderColor: props.selected ? 'fullBlack' : 'gray',
                borderWidth: '1px',
                marginRight: -1,
                _focus: {outline: 'none', zIndex: 2}
            }
        }),
        size: (props) => ({
            swatch: {
                height: '38px',
                width: '38px',
                minWidth: 0,
                borderRadius: 0,
                padding: 0,
                cursor: 'pointer',
                color: props.selected ? (props.disabled ? 'black' : 'white') : 'black',
                borderWidth: '1px',
                borderColor: props.selected ? 'fullBlack' : 'mediumGray',
                backgroundColor: props.selected
                    ? props.disabled
                        ? 'gray'
                        : 'fullBlack'
                    : 'transparent',
                backgroundImage: `${
                    props.disabled
                        ? `${
                              props.selected
                                  ? 'linear-gradient(to top left, transparent calc(50% - 1px), mediumGray, transparent calc(50% + 1px) )'
                                  : 'linear-gradient(to top left, white calc(50% - 1px), mediumGray, white calc(50% + 1px) )'
                          } `
                        : ''
                }`,
                fontSize: '10px'
            },
            swatchesWrapper: {
                gridGap: '8px',
                gridTemplateColumns: 'repeat(auto-fill, 38px)'
            }
        }),
        gender: {
            swatch: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '40px',
                px: {base: '20px', lg: '24px'},
                borderWidth: '1px',
                fontSize: 'md',
                fontFamily: 'body',
                fontWeight: 500,
                textTransform: 'uppercase',
                color: 'darkGray',
                borderColor: 'gray',
                marginRight: {base: -1, lg: 0},
                zIndex: 0,
                _checked: {
                    color: 'fullBlack',
                    borderColor: 'fullBlack',
                    zIndex: 1
                },
                _focus: {outline: 'none', zIndex: 2}
            }
        },
        buttonWithChip: (props) => ({
            swatch: {
                display: 'flex',
                flexDirection: 'column',
                height: '60px',
                flex: 1,
                px: 0,
                width: 'calc(100% + 1px)',
                mt: -1,
                minWidth: ['80px', null, '74px'],
                borderColor: 'gray',
                borderStyle: 'solid',
                borderWidth: 1,
                marginRight: -1,
                _focus: {outline: 'none', zIndex: 2},
                backgroundColor: props.selected ? 'fullBlack' : 'transparent'
            },
            chip: {
                height: '15px',
                width: '15px',
                borderRadius: 'full',
                borderColor: 'gray'
            },
            text: {
                textAlign: 'center',
                fontFamily: 'body',
                color: props.selected ? 'white' : 'darkGray',
                mt: 4
            },
            swatchesWrapper: {
                gridGap: '0',
                gridTemplateColumns: [
                    'repeat(auto-fit, minmax(80px, 1fr))',
                    null,
                    'repeat(auto-fit, minmax(74px, 1fr))'
                ]
            }
        })
    },
    parts: ['swatchesWrapper', 'swatch', 'chip', 'text']
}
